import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";
import { TokensFilters, CreateTokenData, UpdateTokenData } from "./types";

// Buscar tokens de um estabelecimento
export const fetchTokens = createAsyncThunk(
  "tokens/fetchTokens",
  async (
    {
      establishmentId,
      params,
    }: { establishmentId: number; params?: TokensFilters },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.get(
        `/admin/establishments/${establishmentId}/tokens`,
        { params },
      );
      return { ...response.data, establishmentId };
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || error.message,
        });
      } else {
        return rejectWithValue({
          error: "Erro ao carregar tokens",
        });
      }
    }
  },
);

// Criar novo token
export const createToken = createAsyncThunk(
  "tokens/createToken",
  async (
    {
      establishmentId,
      data,
    }: { establishmentId: number; data: CreateTokenData },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.post(
        `/admin/establishments/${establishmentId}/tokens`,
        data,
      );
      return response.data;
    } catch (error: any) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || "Erro ao criar token",
          status: error.response?.status,
        });
      }

      return rejectWithValue({
        error: "Erro inesperado ao criar token",
      });
    }
  },
);

// Atualizar token
export const updateToken = createAsyncThunk(
  "tokens/updateToken",
  async (
    {
      establishmentId,
      tokenId,
      data,
    }: {
      establishmentId: number;
      tokenId: number;
      data: UpdateTokenData;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/admin/establishments/${establishmentId}/tokens/${tokenId}`,
        data,
      );
      return response.data;
    } catch (error: any) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || "Erro ao atualizar token",
          status: error.response?.status,
        });
      }

      return rejectWithValue({
        error: "Erro inesperado ao atualizar token",
      });
    }
  },
);

// Excluir token
export const deleteToken = createAsyncThunk(
  "tokens/deleteToken",
  async (
    {
      establishmentId,
      tokenId,
    }: {
      establishmentId: number;
      tokenId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.delete(
        `/admin/establishments/${establishmentId}/tokens/${tokenId}`,
      );
      return { ...response.data, tokenId };
    } catch (error: any) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || "Erro ao excluir token",
          status: error.response?.status,
        });
      }

      return rejectWithValue({
        error: "Erro inesperado ao excluir token",
      });
    }
  },
);

// Regenerar token
export const regenerateToken = createAsyncThunk(
  "tokens/regenerateToken",
  async (
    {
      establishmentId,
      tokenId,
    }: {
      establishmentId: number;
      tokenId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.post(
        `/admin/establishments/${establishmentId}/tokens/${tokenId}/regenerate`,
      );
      return response.data;
    } catch (error: any) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || "Erro ao regenerar token",
          status: error.response?.status,
        });
      }

      return rejectWithValue({
        error: "Erro inesperado ao regenerar token",
      });
    }
  },
);
